/**
 * Cloudflare Image Transformations URL helper.
 *
 * Listing photos live behind photos.bluekeys.co (Cloudflare in front of R2).
 * When "Image Transformations" is enabled on that zone, prefixing the path with
 * `/cdn-cgi/image/<opts>/` returns a resized + reformatted variant — so a card
 * ships a ~640px WebP (~40 KB) instead of the raw 2800×3500 original (~500 KB,
 * ~38 MB decoded, the reason covers are force-lazy-loaded today).
 *
 * SAFETY: this is a NO-OP passthrough unless NEXT_PUBLIC_CF_IMAGE === "1". That
 * lets the wiring ship now and stay inert until the zone toggle is on — flip
 * the env var (Vercel → Settings → Environment Variables) once "Image
 * Transformations" is enabled in the Cloudflare dashboard, and every wired
 * <img> starts requesting a resized variant with no code change.
 *
 * Why Cloudflare and not Next/Vercel image optimization: the photos already sit
 * behind Cloudflare, transformations are billed once per unique (image, size)
 * per month (5,000 free, then $0.50/1,000 — pennies at this catalog size), and
 * delivery stays on Cloudflare's meter instead of moving onto Vercel Fast Data
 * Transfer. See docs/superpowers/findings for the cost analysis.
 */

/**
 * TEMPORARY KILL SWITCH — 2026-08-13.
 *
 * Cloudflare began returning `HTTP 429 / cf-resized: err=9422` ("Free unique
 * transformations by account has been exhausted") for EVERY transform on this
 * zone, so every listing gallery on bluekeys.co rendered blank (bn-5858: 29 of
 * 31 images broken; a freshly published unit: 29 of 29). The raw R2 objects
 * were fine — 200, full size — so only the transform layer was failing.
 *
 * Cloudflare fails CLOSED on quota: it returns the error instead of passing the
 * original through, so there is no graceful degradation to rely on. Verified
 * with never-before-requested widths (713/661) and `cf-cache-status: MISS`,
 * which rules out stale cached errors — while the dashboard simultaneously
 * reported 0 unique transformations used for the period.
 *
 * Until the Images paid plan is active, bypass the transform layer entirely and
 * serve raw R2. Cost: full-size originals (~0.5-1 MB each) instead of ~40 KB
 * variants, i.e. heavier pages — but visible photos beat fast blank tiles.
 *
 * TO REVERT: set this back to `false` (leaving NEXT_PUBLIC_CF_IMAGE=1) once
 * billing is enabled, and confirm a transformed URL returns 200.
 * See bluekeys-brain L-103.
 */
// Annotated `boolean` rather than left to infer the literal `true`: without it
// TypeScript narrows ENABLED to `false`, which makes the rest of cfImage()
// unreachable code and fails the build.
const FORCE_BYPASS_PENDING_IMAGES_QUOTA: boolean = true;

const ENABLED =
  process.env.NEXT_PUBLIC_CF_IMAGE === "1" && !FORCE_BYPASS_PENDING_IMAGES_QUOTA;
/** Our R2 custom domain — the only host cfImage() can resize (sized
 *  derivatives exist here, not on the legacy `pub-…r2.dev` host). */
export const PHOTO_HOST = "photos.bluekeys.co";

/**
 * PRE-RENDERED R2 DERIVATIVES — 2026-08-17. The replacement for the metered
 * Cloudflare transformer, and the reason the bypass above can stay on forever.
 *
 * The bypass fixed blank tiles but left every surface serving raw originals.
 * Measured on production /search: median 4.9 MP, 20% of loaded card photos
 * over 12 MP, worst 8064×6048 — **195 MB of decoded bitmap for a 395 px card
 * slot**. Combined with "Show more" never releasing what it appended, iOS
 * jetsam-killed the tab and Chrome showed its sad-tab "Can't open this page"
 * (Maged, 2026-08-17). Bytes were never the problem — PIXELS were.
 *
 * `scripts/r2-make-derivatives.mjs` pre-renders two fixed rungs into the same
 * bucket, once, for free:
 *
 *   <key>.jpg → _w800/<key>.jpg.webp    cards, tour tiles, hero  (~1.9 MB decoded)
 *              _w1600/<key>.jpg.webp    lightbox zoom
 *
 * The full original key (extension included) is kept inside the rung prefix so
 * `rawImage()` can always reverse the mapping. A key that hasn't been
 * processed yet just 404s, and every call site falls back to the raw URL on
 * error — so this degrades to today's behaviour instead of breaking.
 *
 * Unlike Cloudflare transformations these are ordinary R2 objects: no monthly
 * quota to exhaust (the L-103 failure mode cannot recur), served as
 * `image/webp` so Cloudflare edge-caches them — the raw originals are
 * `application/octet-stream`, which is why they were always `DYNAMIC`.
 *
 * TO DISABLE: set to false — every path reverts to raw originals.
 */
const R2_DERIVATIVES: boolean = true;

/** Widths actually generated on R2. Keep in sync with the script's RUNGS. */
const R2_RUNGS = [800, 1600];

export interface CfImageOpts {
  /** Target rendered width in CSS px (a sensible DPR bump is applied). */
  width?: number;
  /** JPEG/WebP quality 1–100. Default 80. */
  quality?: number;
  fit?: "cover" | "contain" | "scale-down";
}

/**
 * Rewrite a photos.bluekeys.co URL to a resized Cloudflare variant. Any other
 * host (operator CDNs, Unsplash, already-transformed URLs) is returned
 * untouched, as is any input when the feature flag is off.
 */
export function cfImage(url: string | null | undefined, opts: CfImageOpts = {}): string {
  if (!url) return url ?? "";
  if (!ENABLED && !R2_DERIVATIVES) return url;
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return url; // relative/local asset — leave it
  }
  if (u.hostname !== PHOTO_HOST) return url;
  if (u.pathname.startsWith("/cdn-cgi/")) return url; // already transformed
  if (u.pathname.startsWith("/_w")) return url; // already a derivative

  if (R2_DERIVATIVES) {
    // Snap UP to the nearest generated rung. NO DPR multiplier here (the
    // Cloudflare path below applies one): with only two rungs a 1.5× bump
    // pushes an 800 px card onto the 1600 rung and quadruples its decoded
    // size for no visible gain. Callers already pass a retina-generous width.
    const want = opts.width ?? 640;
    const rung = R2_RUNGS.find((r) => r >= want) ?? R2_RUNGS[R2_RUNGS.length - 1];
    return `${u.origin}/_w${rung}${u.pathname}.webp${u.search}`;
  }

  // Bump for high-DPR screens but cap so we never up-render past the source.
  const width = Math.min(Math.round((opts.width ?? 640) * 1.5), 2000);
  const params = [
    `width=${width}`,
    `quality=${Math.round(opts.quality ?? 80)}`,
    `fit=${opts.fit ?? "cover"}`,
    "format=auto",
  ].join(",");
  return `${u.origin}/cdn-cgi/image/${params}${u.pathname}${u.search}`;
}

/** True when resized variants are actually being served — either pre-rendered
 *  R2 derivatives or live Cloudflare transformations. */
export const CF_IMAGE_ENABLED = ENABLED || R2_DERIVATIVES;

/**
 * Strip a `/_w<width>/…​.webp` derivative prefix or a `/cdn-cgi/image/<opts>/`
 * transform prefix back to the raw origin URL.
 * Used as an <img> onError fallback: Cloudflare image transformations are
 * metered (5,000 unique/month free), and once the month's allowance is spent
 * every NEW (uncached) transform returns 429 → a broken card image. Falling
 * back to the pre-made WebP (which always 200s straight from R2) keeps images
 * visible until the quota resets or a paid Images plan is added. No-op for a
 * URL that isn't a transform URL.
 */
export function rawImage(url: string | null | undefined): string {
  if (!url) return url ?? "";
  try {
    const u = new URL(url);
    // Pre-rendered derivative: /_w800/<original key>.webp → /<original key>.
    // The original extension is preserved inside the key, so dropping the rung
    // prefix and the trailing `.webp` reconstructs it exactly.
    const d = u.pathname.match(/^\/_w\d+(\/.+)\.webp$/);
    if (d) return `${u.origin}${d[1]}${u.search}`;
    const m = u.pathname.match(/^\/cdn-cgi\/image\/[^/]+(\/.*)$/);
    return m ? `${u.origin}${m[1]}${u.search}` : url;
  } catch {
    return url;
  }
}
